import { demoFromHealth, type Health } from './autoMode';

export interface AppConfig {
  /** "/api/v1" here, or "https://deleg8-dev.example/api/v1". Sign-in is at its origin's /auth. */
  apiBase: string;
  /** Answer every call in the browser (demo data; nothing leaves the page). config.json may say "auto":
   *  the demo only when this site clearly has no Deleg8 (autoMode.ts); a slow or failing one is still the real API. */
  mock: boolean;
  st8nUrl?: string;
  /** False when this server runs without st8n (healthz `st8n: false`): the page hides every st8n part. */
  st8nOn: boolean;
  /** "Sign in with st8n" is fully set up here (healthz `signIn.st8n`): st8n can be on with its sign-in off. The demo: st8nOn. */
  st8nSignIn?: boolean;
  /** The company name healthz reports for the portal's header (Eli's brand), else Deleg8. */
  brandName?: string;
  /** Local development against DELEG8_DEV_LOGIN=on: sign in as this email. Honoured on localhost only. */
  devLoginEmail?: string;
  /** A customer-facing install (DELEG8_FRONT=portal): Sign in at /, sign-up through an invite only, no st8n words. */
  front?: 'portal';
}

export let config: AppConfig = { apiBase: '/api/v1', mock: true, st8nOn: true, st8nUrl: 'https://dev.st8n.ai' };

export const authBase = () => config.apiBase.replace(/\/api\/v1\/?$/, '') + '/auth';

/** /config.json (deploy time), then ?mock=1|0 and, on localhost, ?devLogin= (kept for this tab). */
export async function loadConfig(): Promise<AppConfig> {
  let auto = false;
  try {
    const r = await fetch('/config.json', { cache: 'no-store' });
    if (r.ok) {
      const j = (await r.json()) as Omit<AppConfig, 'mock'> & { mock?: boolean | 'auto' };
      auto = j.mock === 'auto';
      config = { ...config, ...j, mock: j.mock === 'auto' ? true : (j.mock ?? config.mock) };
    }
  } catch {
    // No network (an installed app opened offline): decide as "auto" does, so it is the real app's offline screen, never the demo.
    auto = true;
  }
  const q = new URLSearchParams(location.search);
  const local = ['localhost', '127.0.0.1'].includes(location.hostname);
  const keep = (k: string) => {
    try {
      const v = q.get(k);
      if (v !== null) sessionStorage.setItem('deleg8.' + k, v);
      return sessionStorage.getItem('deleg8.' + k);
    } catch {
      return q.get(k);
    }
  };
  const mock = keep('mock');
  if (mock === '1' || mock === '0') config.mock = mock === '1';
  else if (auto) config.mock = demoFromHealth(await health());
  if (!config.mock) {
    const h = await health();
    const body = h.kind === 'answer' ? (h.body as { st8n?: unknown; brand?: { name?: unknown }; brandName?: unknown; signIn?: { front?: unknown; brand?: unknown; st8n?: unknown } } | null) : null;
    if (body && body.st8n === false) config.st8nOn = false;
    config.st8nSignIn = body?.signIn?.st8n === true;
    // A portal (a customer's own address, or a portal install) is named for its company (healthz signIn.brand).
    const n = (body?.signIn?.front === 'portal' ? body.signIn.brand : undefined) ?? body?.brand?.name ?? body?.brandName;
    if (typeof n === 'string' && n.trim()) config.brandName = n.trim().slice(0, 60);
  }
  keep('client');
  if (keep('st8n') === '0') config.st8nOn = false;
  const dev = local ? keep('devLogin') : null;
  if (dev) config.devLoginEmail = dev;
  return config;
}

/** What this site's GET /healthz says (Deleg8 answers { ok, signIn }); 4 s at most. */
async function health(): Promise<Health> {
  try {
    const r = await fetch(config.apiBase.replace(/\/api\/v1\/?$/, '') + '/healthz', { cache: 'no-store', signal: AbortSignal.timeout(4000) });
    let body: unknown = null;
    try {
      body = await r.json();
    } catch {
      body = null;
    }
    return { kind: 'answer', status: r.status, body };
  } catch {
    return { kind: 'unreachable' };
  }
}
